import * as Sentry from "@sentry/react";
import { useEffect } from "react";

import { config } from "../config";

export const initSentry = () => {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: true,
        maskAllText: false,
        blockAllMedia: false,
        // Web dashboards render the main content inside an iframe (`WebDashboardViewer` uses `srcDoc`).
        // Without unblocking, Sentry Replay can record an effectively blank page.
        unblock: ["iframe"],
        unmask: [".ag-header"],
      }),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
    enableLogs: true,
    beforeSendLog: () => null,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Cannot read properties of undefined (reading 'points')",
      "Network Error",
      "canceled",
      "Cannot read properties of null (reading 'focus')",
      "table missing in",
      "Unable to find node on an unmounted component.",
      "OfficeRuntime.auth.getAccessToken timed out.",
      "Cannot read properties of undefined (reading 'register')",
      "Excel is in cell-editing mode.",
      "ControlIdNotFound",
      "Service Spreadsheets failed while accessing document",
      "Request failed with status code",
      "Operation failed because the data is not current on the server.",
      "Failed to load https://apis.google.com/js/api.js",
    ],
  });

  Sentry.setTag("coeffPlatform", "webapp");
};

export const useSentryLogging = (enabled: boolean) => {
  useEffect(() => {
    const client = Sentry.getClient();
    if (!client) {
      return;
    }

    const options = client.getOptions();
    options.beforeSendLog = enabled ? log => log : () => null;
  }, [enabled]);
};

export const usePageVisibilityLogger = () => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      Sentry.logger.info("Page visibility changed", {
        visibilityState: document.visibilityState,
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};

export const useSentryMountUnmountTransaction = (options: { name: string; op?: string }) => {
  useEffect(() => {
    const span = Sentry.startInactiveSpan({
      name: options.name,
      op: options.op ?? "mount/unmount",
      forceTransaction: true,
    });

    return () => {
      span.end();
    };
  }, []);
};

export const getCurrentReplayId = (): string | undefined => {
  const client = Sentry.getClient();
  const replay =
    client?.getIntegrationByName<ReturnType<typeof Sentry.replayIntegration>>("Replay");
  // @ts-ignore - getReplayId may exist on the integration instance
  return replay?.getReplayId?.();
};

type EventProperties = {
  id: string;
  class?: string;
};

export const addDummyUserClickEvent = (eventProps: EventProperties) => {
  const buttonElement = document.createElement("event");

  buttonElement.id = eventProps.id;

  if (eventProps.class) {
    buttonElement.className = eventProps.class;
  }

  buttonElement.onclick = e => {
    e.stopPropagation();
  };

  document.body.appendChild(buttonElement);

  setTimeout(() => {
    const click = new MouseEvent("click");
    buttonElement.dispatchEvent(click);
  }, 0);
};

interface SentryCaptureProps {
  context?: { [x: string]: unknown };
  error: unknown;
  name?: string;
  screen?: string;
}

export const sentryCapture = ({ context, error, name, screen }: SentryCaptureProps) => {
  Sentry.captureException(error, scope => {
    if (name) {
      scope.setTransactionName(name);
    }

    if (screen) {
      scope.setExtra("screen", screen);
    }

    if (context) {
      scope.setContext("coeffctx", context);
    }

    return scope;
  });
};
